// TODO: Move histogram generation into layer metadata calculations from the API.

import {
  BAD_COLOR,
  FIVE_STOP_RANGE,
  getFiveStopPaintStyle,
  getThreeStopPaintStyle,
  getTwoStopPaintStyle,
  GOOD_COLOR,
  makeDataCategoriesValueMatch,
  NA_COLOR,
  NEUTRAL_PALETTE,
  NutrientManagementDatatype,
  OPINIONATED_PALETTE,
  THREE_STOP_VALUES_LABELS,
  TWO_STOP_VALUES_LABELS,
  WARN_COLOR,
} from "./types";

// Create a style given an upper/lower bound for the data range and an array
// of colors. This splits the range into equal buckets and assigns a color to each one.
const getBoundedRangeStyle = (
  dataKey: string,
  upperBound: number = 100,
  lowerBound: number = 0,
  altBucketColors?: string[],
) => {
  if (!isFinite(upperBound) || !isFinite(lowerBound)) {
    return [NA_COLOR];
  }
  const range = upperBound - lowerBound;
  const bucketColors = altBucketColors || NEUTRAL_PALETTE;
  const step = range / bucketColors.length;
  const stepArray = [];
  for (let i = 0; i < bucketColors.length; i++) {
    stepArray.push(lowerBound + step * i);
    stepArray.push(bucketColors[i]);
  }
  return [["get", dataKey], ...stepArray];
};

const percentageCategories = [
  {
    id: "0to20",
    label: "Saturation is 0-20%",
    valueMatch: (value: number) => value < 20,
    color: [NEUTRAL_PALETTE[0]],
  },
  {
    id: "20to40",
    label: "Saturation is 20-40%",
    valueMatch: (value: number) => value >= 20 && value < 40,
    color: [NEUTRAL_PALETTE[1]],
  },
  {
    id: "40to60",
    label: "Saturation is 40-60%",
    valueMatch: (value: number) => value >= 40 && value < 60,
    color: [NEUTRAL_PALETTE[2]],
  },
  {
    id: "60to80",
    label: "Saturation is 60-80%",
    valueMatch: (value: number) => value >= 60 && value < 80,
    color: [NEUTRAL_PALETTE[3]],
  },
  {
    id: "80to100",
    label: "Saturation is 80-100%",
    valueMatch: (value: number) => value >= 80,
    color: [NEUTRAL_PALETTE[4]],
  },
];

export const NUTRIENT_TESTING_DATATYPES: NutrientManagementDatatype[] = [
  {
    name: "CEC",
    id: "vendor_cec_meq_100_g",
    visualizationKey: "vendor_cec_meq_100_g",
    categorizationKey: "cec_range",
    mapboxPaintStyle: {
      "fill-color": ["match", ...getFiveStopPaintStyle("cec_range")],
    },
    categorization: makeDataCategoriesValueMatch(
      OPINIONATED_PALETTE,
      FIVE_STOP_RANGE,
    ),
    histogramTitle: "CEC distribution across all fields (percent of acres):",
    description: (
      <>
        <div>
          <span className="font-bold">Where is this data from?</span>
          <p>Annual nutrient testing, conducted by Vayda.</p>
        </div>
        <p>Values shown on map are for CEC (meq/100g).</p>
      </>
    ),
    partnerLayerIds: ["rx_k2o_general", "rx_lime"],
  },
  {
    name: "pH (water)",
    id: "vendor_ph_h20",
    visualizationKey: "vendor_ph_h20",
    categorizationKey: "ph_range",
    mapboxPaintStyle: {
      "fill-color": ["match", ...getThreeStopPaintStyle("ph_range")],
    },
    histogramTitle:
      "pH (water) distribution across all fields (percent of acres):",
    categorization: makeDataCategoriesValueMatch(
      [BAD_COLOR, WARN_COLOR, GOOD_COLOR],
      THREE_STOP_VALUES_LABELS,
    ),
    partnerLayerIds: ["rx_lime"],
  },
  {
    name: "pH (buffer)",
    id: "vendor_ph_buffer",
    visualizationKey: "vendor_ph_buffer",
    categorizationKey: "ph_b_range",
    mapboxPaintStyle: {
      "fill-color": ["match", ...getThreeStopPaintStyle("ph_b_range")],
    },
    histogramTitle:
      "pH (buffer) distribution across all fields (percent of acres):",
    categorization: makeDataCategoriesValueMatch(
      [BAD_COLOR, WARN_COLOR, GOOD_COLOR],
      THREE_STOP_VALUES_LABELS,
    ),
  },
  {
    name: "Organic matter",
    id: "vender_om",
    visualizationKey: "vender_om",
    categorizationKey: "om_range",
    mapboxPaintStyle: {
      "fill-color": ["match", ...getFiveStopPaintStyle("om_range")],
    },
    categorization: makeDataCategoriesValueMatch(
      OPINIONATED_PALETTE,
      FIVE_STOP_RANGE,
    ),
    histogramTitle:
      "Organic matter distribution across all fields (percent of acres):",
    description: (
      <>
        <div>
          <span className="font-bold">Where is this data from?</span>
          <p>Annual nutrient testing, conducted by Vayda.</p>
        </div>
        <p>Values shown on map are for Organic matter (%).</p>
      </>
    ),
    partnerLayerIds: ["rx_s"],
  },
  {
    name: "Phosphorus (P)",
    id: "vendor_p_ppm",
    visualizationKey: "vendor_p_ppm",
    categorizationKey: "p_range",
    mapboxPaintStyle: {
      "fill-color": ["match", ...getFiveStopPaintStyle("p_range")],
    },
    partnerLayerIds: ["rx_p2o5_general"],
    categorization: makeDataCategoriesValueMatch(
      OPINIONATED_PALETTE,
      FIVE_STOP_RANGE,
    ),
    histogramTitle:
      "Phosphorus distribution across all fields (percent of acres):",
    description: (
      <>
        <div>
          <span className="font-bold">Where is this data from?</span>
          <p>Annual nutrient testing, conducted by Vayda.</p>
        </div>
        <p>Values shown on map are for Phosphorus (ppm).</p>
      </>
    ),
  },
  {
    name: "Potassium (K)",
    id: "vendor_k_ppm",
    visualizationKey: "vendor_k_ppm",
    categorizationKey: "k_range",
    mapboxPaintStyle: {
      "fill-color": ["match", ...getFiveStopPaintStyle("k_range")],
    },
    partnerLayerIds: ["rx_k2o_general"],
    categorization: makeDataCategoriesValueMatch(
      OPINIONATED_PALETTE,
      FIVE_STOP_RANGE,
    ),
    histogramTitle:
      "Potassium distribution across all fields (percent of acres):",
    description: (
      <>
        <div>
          <span className="font-bold">Where is this data from?</span>
          <p>Annual nutrient testing, conducted by Vayda.</p>
        </div>
        <p>Values shown on map are for Potassium (ppm).</p>
      </>
    ),
  },
  {
    name: "Calcium (Ca)",
    id: "vendor_ca_ppm",
    visualizationKey: "vendor_ca_ppm",
    categorizationKey: "ca_range",
    mapboxPaintStyle: {
      "fill-color": ["match", ...getTwoStopPaintStyle("ca_range")],
    },
    categorization: makeDataCategoriesValueMatch(
      [BAD_COLOR, GOOD_COLOR],
      TWO_STOP_VALUES_LABELS,
    ),
    histogramTitle:
      "Calcium distribution across all fields (percent of acres):",
    description: (
      <>
        <div>
          <span className="font-bold">Where is this data from?</span>
          <p>Annual nutrient testing, conducted by Vayda.</p>
        </div>
        <p>Values shown on map are for Calcium (ppm).</p>
      </>
    ),
  },
  {
    name: "Magnesium (Mg)",
    id: "vendor_mg_ppm",
    visualizationKey: "vendor_mg_ppm",
    categorizationKey: "mg_range",
    mapboxPaintStyle: {
      "fill-color": ["match", ...getFiveStopPaintStyle("mg_range")],
    },
    categorization: makeDataCategoriesValueMatch(
      OPINIONATED_PALETTE,
      FIVE_STOP_RANGE,
    ),
    histogramTitle:
      "Magnesium distribution across all fields (percent of acres):",
    description: (
      <>
        <div>
          <span className="font-bold">Where is this data from?</span>
          <p>Annual nutrient testing, conducted by Vayda.</p>
        </div>
        <p>Values shown on map are for Magnesium (ppm).</p>
      </>
    ),
  },
  {
    name: "Sulfur (S)",
    id: "vendor_s_ppm",
    visualizationKey: "vendor_s_ppm",
    categorizationKey: "s_range",
    mapboxPaintStyle: {
      "fill-color": ["match", ...getTwoStopPaintStyle("s_range")],
    },
    categorization: makeDataCategoriesValueMatch(
      [BAD_COLOR, GOOD_COLOR],
      TWO_STOP_VALUES_LABELS,
    ),
    histogramTitle: "Sulfur distribution across all fields (percent of acres):",
    description: (
      <>
        <div>
          <span className="font-bold">Where is this data from?</span>
          <p>Annual nutrient testing, conducted by Vayda.</p>
        </div>
        <p>Values shown on map are for Sulfur (ppm).</p>
      </>
    ),
  },
  // TODO: Add Sodium maps when we get data ranges
  // {
  //   name: "Sodium (Na)",
  //   id: "vendor_na_ppm",
  //   visualizationKey: "vendor_na_ppm",
  //   mapboxPaintStyle: {},
  // },
  {
    name: "Zinc (Zn)",
    id: "vendor_zn_ppm",
    visualizationKey: "vendor_zn_ppm",
    categorizationKey: "zn_range",
    mapboxPaintStyle: {
      "fill-color": ["match", ...getFiveStopPaintStyle("zn_range")],
    },
    categorization: makeDataCategoriesValueMatch(
      OPINIONATED_PALETTE,
      FIVE_STOP_RANGE,
    ),
    histogramTitle: "Zinc distribution across all fields (percent of acres):",
    description: (
      <>
        <div>
          <span className="font-bold">Where is this data from?</span>
          <p>Annual nutrient testing, conducted by Vayda.</p>
        </div>
        <p>Values shown on map are for Zinc (ppm).</p>
      </>
    ),
  },
  // TODO: Add Iron maps when we get data ranges
  // {
  //   name: "Iron (Fe)",
  //   id: "vendor_fe_ppm",
  //   visualizationKey: "vendor_fe_ppm",
  //   mapboxPaintStyle: {},
  // },
  {
    name: "Manganese (Mn)",
    id: "vendor_mn_ppm",
    visualizationKey: "vendor_mn_ppm",
    categorizationKey: "mn_range",
    mapboxPaintStyle: {
      "fill-color": ["match", ...getTwoStopPaintStyle("mn_range")],
    },
    categorization: makeDataCategoriesValueMatch(
      [BAD_COLOR, GOOD_COLOR],
      TWO_STOP_VALUES_LABELS,
    ),
    histogramTitle:
      "Manganese distribution across all fields (percent of acres):",
    description: (
      <>
        <div>
          <span className="font-bold">Where is this data from?</span>
          <p>Annual nutrient testing, conducted by Vayda.</p>
        </div>
        <p>Values shown on map are for Manganese (ppm).</p>
      </>
    ),
  },
  {
    name: "Copper (Cu)",
    id: "vendor_cu_ppm",
    visualizationKey: "vendor_cu_ppm",
    categorizationKey: "cu_range",
    mapboxPaintStyle: {
      "fill-color": ["match", ...getTwoStopPaintStyle("cu_range")],
    },
    categorization: makeDataCategoriesValueMatch(
      [BAD_COLOR, GOOD_COLOR],
      TWO_STOP_VALUES_LABELS,
    ),
    histogramTitle: "Copper distribution across all fields (percent of acres):",
    description: (
      <>
        <div>
          <span className="font-bold">Where is this data from?</span>
          <p>Annual nutrient testing, conducted by Vayda.</p>
        </div>
        <p>Values shown on map are for Copper (ppm).</p>
      </>
    ),
  },
  {
    name: "Boron (B)",
    id: "vendor_b_ppm",
    visualizationKey: "vendor_b_ppm",
    categorizationKey: "b_range",
    mapboxPaintStyle: {
      "fill-color": ["match", ...getThreeStopPaintStyle("b_range")],
    },
    categorization: makeDataCategoriesValueMatch(
      [BAD_COLOR, WARN_COLOR, GOOD_COLOR],
      THREE_STOP_VALUES_LABELS,
    ),
    histogramTitle: "Boron distribution across all fields (percent of acres):",
    description: (
      <>
        <div>
          <span className="font-bold">Where is this data from?</span>
          <p>Annual nutrient testing, conducted by Vayda.</p>
        </div>
        <p>Values shown on map are for Boron (ppm).</p>
      </>
    ),
  },
  {
    name: "Calcium saturation (Ca_sat)",
    id: "vendor_ca_sat_pct",
    visualizationKey: "vendor_ca_sat_pct",
    mapboxPaintStyle: {
      "fill-color": [
        "interpolate",
        ["linear"],
        ...getBoundedRangeStyle("vendor_ca_sat_pct"),
      ],
    },
    categorization: percentageCategories,
    histogramTitle: "Farm acres that test as...",
    description: (
      <>
        <div>
          <span className="font-bold">Where is this data from?</span>
          <p>Annual nutrient testing, conducted by Vayda.</p>
        </div>
        <p>Values shown on map are for Calcium saturation (%).</p>
      </>
    ),
  },
  {
    name: "Magnesium saturation (Mg_sat)",
    id: "vendor_mg_sat_pct",
    visualizationKey: "vendor_mg_sat_pct",
    mapboxPaintStyle: {
      "fill-color": [
        "interpolate",
        ["linear"],
        ...getBoundedRangeStyle("vendor_mg_sat_pct"),
      ],
    },
    categorization: percentageCategories,
    histogramTitle: "Farm acres that test as...",
    description: (
      <>
        <div>
          <span className="font-bold">Where is this data from?</span>
          <p>Annual nutrient testing, conducted by Vayda.</p>
        </div>
        <p>Values shown on map are for Magnesium saturation (%).</p>
      </>
    ),
  },
  {
    name: "Potassium saturation (K_sat)",
    id: "vendor_k_sat_pct",
    visualizationKey: "vendor_k_sat_pct",
    mapboxPaintStyle: {
      "fill-color": [
        "interpolate",
        ["linear"],
        ...getBoundedRangeStyle("vendor_k_sat_pct"),
      ],
    },
    categorization: percentageCategories,
    histogramTitle: "Farm acres that test as...",
    description: (
      <>
        <div>
          <span className="font-bold">Where is this data from?</span>
          <div>Annual nutrient testing, conducted by Vayda.</div>
        </div>
        <p>Values shown on map are for Potassium saturation (%).</p>
      </>
    ),
  },
  {
    name: "Base saturation",
    id: "base_sat",
    visualizationKey: "base_sat",
    mapboxPaintStyle: {
      "fill-color": [
        "interpolate",
        ["linear"],
        ...getBoundedRangeStyle("base_sat", 100, 0, OPINIONATED_PALETTE),
      ],
    },
    categorization: [
      {
        id: "0to20",
        label: "Saturation is 0-20%",
        valueMatch: (value) => value < 20,
        color: [OPINIONATED_PALETTE[0]],
      },
      {
        id: "20to40",
        label: "Saturation is 20-40%",
        valueMatch: (value) => value >= 20 && value < 40,
        color: [OPINIONATED_PALETTE[1]],
      },
      {
        id: "40to60",
        label: "Saturation is 40-60%",
        valueMatch: (value) => value >= 40 && value < 60,
        color: [OPINIONATED_PALETTE[2]],
      },
      {
        id: "60to80",
        label: "Saturation is 60-80%",
        valueMatch: (value) => value >= 60 && value < 80,
        color: [OPINIONATED_PALETTE[3]],
      },
      {
        id: "80to100",
        label: "Saturation is 80-100%",
        valueMatch: (value) => value >= 80,
        color: [OPINIONATED_PALETTE[4]],
      },
    ],
    histogramTitle: "Farm acres that test as...",
    description: (
      <>
        <div>
          <span className="font-bold">Where is this data from?</span>
          <p>Annual nutrient testing, conducted by Vayda.</p>
        </div>
        <p>Values shown on map are for Base saturation (%).</p>
      </>
    ),
    partnerLayerIds: ["rx_lime"],
  },
  // TODO: Add these back in when we begin generating recomendation maps for them
  // {
  //   name: "Sodium saturation (Na_sat)",
  //   id: "vendor_na_sat_pct",
  //   visualizationKey: "vendor_na_sat_pct",
  //   mapboxPaintStyle: (upper, lower) => ({
  //     "fill-color": [
  //       "interpolate",
  //       ["linear"],
  //       ...getBoundedRangeStyle("vendor_na_sat_pct", upper, lower),
  //     ],
  //   }),
  // },
  // {
  //   name: "Hydrogen saturation (H_sat)",
  //   id: "vendor_h_sat_pct",
  //   visualizationKey: "vendor_h_sat_pct",
  //   mapboxPaintStyle: (upper, lower) => ({
  //     "fill-color": [
  //       "interpolate",
  //       ["linear"],
  //       ...getBoundedRangeStyle("vendor_h_sat_pct", upper, lower),
  //     ],
  //   }),
  // },
];
